import getFirebase from "../../../api/firebase";
import { getProductType } from "../../../product/api";
import type { Product, SaveProductParams } from "./types";
import type { OptionConfig } from "../../../product/types";

function mapValues<T, U>(
  obj: Record<string, T> | undefined,
  fn: (value: T, key: string) => U,
): Record<string, U> {
  if (!obj) return {};
  return Object.fromEntries(Object.entries(obj).map(([key, value]) => [key, fn(value, key)]));
}

function mapKeys<T>(
  obj: Record<string, T> | undefined,
  fn: (value: T, key: string) => string | number,
): Record<string | number, T> {
  if (!obj) return {};
  return Object.fromEntries(Object.entries(obj).map(([key, value]) => [fn(value, key), value]));
}

function normalizeProduct(product: Product, userId: string): Product {
  const productType = getProductType(product.name);

  if (!productType) {
    console.warn("Unknown product type " + product.name);
    return product;
  }

  const oldKeys: Record<string, number> = {
    first: 0,
    second: 1,
    third: 2,
    four: 3,
  };

  return {
    ...product,
    userId: userId || product.userId,
    options: {
      ...mapValues(
        productType.options as Record<string, OptionConfig>,
        ({ defaultValue }, key) => product.options[key] || defaultValue,
      ),
      pages: mapValues(product.options?.pages, (page: any) => {
        return {
          ...page,
          photos: mapKeys(page.photos, (_value, photoKey) => {
            return oldKeys[photoKey] !== undefined ? oldKeys[photoKey] : photoKey;
          }),
          labels: mapKeys(page.labels, (_value, labelKey) => {
            return oldKeys[labelKey] !== undefined ? oldKeys[labelKey] : labelKey;
          }),
        };
      }),
    },
  };
}

export const saveProductToFirebase = async ({
  product,
  serverChanges = {},
  userId,
}: SaveProductParams): Promise<Product> => {
  const firebase = await getFirebase();

  const productObject = normalizeProduct(
    {
      ...product,
      updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
      createdAt: product.createdAt || firebase.firestore.FieldValue.serverTimestamp(),
    },
    userId,
  );

  const updatedProductObject = {
    ...productObject,
    ...serverChanges,
  };

  await firebase
    .firestore()
    .collection("products")
    .doc(productObject.uid)
    .set(updatedProductObject);

  return updatedProductObject;
};

export const deleteProductFromFirebase = async (uid: string): Promise<void> => {
  const firebase = await getFirebase();

  await firebase.firestore().collection("products").doc(uid).delete();
};
